import request from '@/config/axios'
import { asCapacityPage, type CapacityPageResult } from '@/api/capacity/page'

export interface WalletView {
  phone: string
  available: number
  frozen?: number
  cardLast4?: string
  realName?: string
  cardStatus?: string
  pendingCardLast4?: string
  pendingRealName?: string
  cardRejectReason?: string
  withdrawFrozen?: boolean
  debt?: number
}

export interface LedgerEntry {
  entryId: string
  phone: string
  type: string
  amount: number
  waybillId?: string
  remark?: string
  createdAt?: string
}

export interface WithdrawRecord {
  withdrawId: string
  phone: string
  amount: number
  cardLast4?: string
  realName?: string
  status: string
  rejectReason?: string
  reviewer?: string
  reviewTime?: string
  ledgerEntryId?: string
  createdAt?: string
}

export interface OrgBill {
  billId: string
  orgId: string
  period: string
  amount: number
  orderCount: number
  status: string
  disputeReason?: string
}

export interface WalletActResult {
  success: boolean
  message?: string
}

export interface ReconcileView {
  externalFeeTotal: number
  riderCreditTotal: number
  orgCommissionTotal: number
  platformFeeTotal: number
  residualHint?: string
}

export const getWallet = (phone: string) =>
  request.get<WalletView>({ url: '/capacity/wallet/rider', params: { phone } })

export const getLedger = (phone: string) =>
  request.get<LedgerEntry[]>({ url: '/capacity/wallet/rider/ledger', params: { phone } })

export const getIncomeCopy = () =>
  request.get<{ text: string }>({ url: '/capacity/wallet/rider/copy' })

export const bindCard = (phone: string, cardLast4: string, realName: string) =>
  request.post<WalletActResult>({
    url: '/capacity/wallet/rider/bind-card',
    params: { phone, cardLast4, realName }
  })

export const withdraw = (phone: string, amount: number) =>
  request.post<WalletActResult>({
    url: '/capacity/wallet/rider/withdraw',
    params: { phone, amount }
  })

export const listPendingBindCards = async (params?: {
  pageNo?: number
  pageSize?: number
}): Promise<CapacityPageResult<WalletView>> => {
  const raw = await request.get({ url: '/capacity/wallet/bind-card/pending', params })
  return asCapacityPage<WalletView>(raw, params?.pageNo, params?.pageSize)
}

export const reviewBindCard = (data: { phone: string; approved: boolean; reason?: string }) =>
  request.post<WalletActResult>({ url: '/capacity/wallet/bind-card/review', data })

export const listWithdraws = async (params?: {
  status?: string
  pageNo?: number
  pageSize?: number
}): Promise<CapacityPageResult<WithdrawRecord>> => {
  const raw = await request.get({ url: '/capacity/wallet/withdraw/pending', params })
  return asCapacityPage<WithdrawRecord>(raw, params?.pageNo, params?.pageSize)
}

export const reviewWithdraw = (data: {
  withdrawId: string
  approved: boolean
  reason?: string
}) => request.post<WalletActResult>({ url: '/capacity/wallet/withdraw/review', data })

export const listOrgBills = (orgId: string) =>
  request.get<OrgBill[]>({ url: '/capacity/wallet/org/bills', params: { orgId } })

export const listAllBills = async (params?: {
  pageNo?: number
  pageSize?: number
}): Promise<CapacityPageResult<OrgBill>> => {
  const raw = await request.get({ url: '/capacity/wallet/org/bills/all', params })
  return asCapacityPage<OrgBill>(raw, params?.pageNo, params?.pageSize)
}

export const confirmBill = (orgId: string, billId: string) =>
  request.post<WalletActResult>({
    url: '/capacity/wallet/org/bills/confirm',
    params: { orgId, billId }
  })

export const disputeBill = (orgId: string, billId: string, reason?: string) =>
  request.post<WalletActResult>({
    url: '/capacity/wallet/org/bills/dispute',
    params: { orgId, billId, reason }
  })

export interface CommissionEntry {
  entryId: string
  orgId: string
  waybillId: string
  amount: number
  billId?: string
  createdAt?: string
}

export const listBillLines = (billId: string) =>
  request.get<CommissionEntry[]>({ url: '/capacity/wallet/org/bills/lines', params: { billId } })

export const disputeBillLine = (orgId: string, billId: string, waybillId: string, reason?: string) =>
  request.post<WalletActResult>({
    url: '/capacity/wallet/org/bills/dispute-line',
    params: { orgId, billId, waybillId, reason }
  })

export const markPaid = (billId: string) =>
  request.post<WalletActResult>({
    url: '/capacity/wallet/org/bills/mark-paid',
    params: { billId }
  })

export const reconcile = () =>
  request.get<ReconcileView>({ url: '/capacity/wallet/reconcile' })
